import {
    AlertListTypeEnum,
    ApplicationHelpType,
    TutorialTopics,
} from '../../../utilities/Constants';
import { Button, Stack } from 'react-bootstrap';
import { ChangeEvent, useState } from 'react';

import Authorization from '../../../stores/Authorization';
import { CheckmateEasyUpdateIcon } from '../../../shared/CheckmateIcons';
import { Favorite } from '../../../components/shared/Favorite';
import { Help } from '../../../components/shared/Help';
import { IAlertSearchModel } from '../../../interfaces/INote';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { useAlertListFilters } from './AlertListFiltersContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

const tutorialLink = LocalRoutes.ViewSpecificPDF.replace(':id', TutorialTopics.Alerts.toString());

export interface IAlertListHeaderProps {
    alertListType: AlertListTypeEnum;
    currentSearchModel: IAlertSearchModel;
    onDefaultFavoriteChange: (settingJson?: string) => void;
    onEasyUpdateChange: (checked: boolean) => void;
    onFavoriteSelected: (settingJson?: string, isDefault?: boolean) => void;
    onRefreshClick: () => void;
    onValidateFavorites: () => boolean;
    pageTitle: string;
}

export function AlertListHeader(props: IAlertListHeaderProps) {
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();

    const { getDisplayTextForSearchModel } = useAlertListFilters();

    const [easyUpdateChecked, setEasyUpdateChecked] = useState<boolean>(false);
    const [selectedFavorite, setSelectedFavorite] = useState<IAlertSearchModel | undefined>(
        undefined
    );

    const handleEasyUpdateChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setEasyUpdateChecked(checked);
        props.onEasyUpdateChange(checked);
    };

    const helpComponent =
        props.alertListType === AlertListTypeEnum.MyAlertList ? (
            <Help
                type={ApplicationHelpType.Info}
                title="My Alert List"
                helpText="Alerts in this view are limited to those alerts that include YOU as the alert target or as part of the watch group.  MY ALERT LIST displays unread (bold font) and read status and you can filter on unread/read status."
            />
        ) : (
            <Help
                type={ApplicationHelpType.Info}
                title="Zone Alert List"
                helpText="This view includes ALL alerts for the zone.  This view does not display unread/read status and you cannot filter on that status; however,  if you read an alert from this view and then see that same alert in MY ALERT LIST, the alert will display as read."
            />
        );

    return (
        <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
            <Stack direction="horizontal" style={{ alignItems: 'center' }}>
                <h1 className="d-inline-block mb-0">{props.pageTitle}</h1>
                {helpComponent}
            </Stack>
            <Stack direction="horizontal">
                <Link target="_blank" to={tutorialLink} className="btn btn-no-bg text-gray">
                    <Stack direction="horizontal" gap={1}>
                        <i className={'fal fa-graduation-cap'} />
                        <span>Tutorials</span>
                    </Stack>
                </Link>
                {Authorization.isAuthorizedToRoute(LocalRoutes.CaseEasyUpdate, user) && (
                    <span className="btn btn-no-bg float-end text-gray ps-3">
                        <input
                            checked={easyUpdateChecked}
                            className="form-check-input"
                            id="EastUpdateCheckbox"
                            name="chkEasyUpdate"
                            onChange={handleEasyUpdateChange}
                            type="checkbox"
                        />
                        <label htmlFor="EastUpdateCheckbox">
                            <CheckmateEasyUpdateIcon className="ps-2 pe-1" />
                            Easy Update
                        </label>
                    </span>
                )}

                {props.alertListType && (
                    <Favorite
                        applyFavoriteSelected={props.onFavoriteSelected}
                        currentFavoriteDetailsString={
                            selectedFavorite ? getDisplayTextForSearchModel(selectedFavorite) : ''
                        }
                        currentFavoriteSettingJson={JSON.stringify(props.currentSearchModel)}
                        onDefaultFavoriteChange={props.onDefaultFavoriteChange}
                        onFavoriteSelected={(settingJson) => {
                            if (settingJson) {
                                setSelectedFavorite(JSON.parse(settingJson));
                            }
                        }}
                        onValidateFilters={props.onValidateFavorites}
                        type={{ id: props.alertListType }}
                        user={user}
                        zoneContext={zoneContext}
                    />
                )}
                <Button variant="primary" onClick={props.onRefreshClick}>
                    Refresh
                </Button>
            </Stack>
        </Stack>
    );
}
